<template>
  <div class="subscribe-content">
    <div class="subscribe-content__wrapper">
      <!-- Header Section -->
      <header class="subscribe-content__header">
        <div class="subscribe-content__brand">
          <LogotypeCapital class="subscribe-content__logo" />
          <CaplsuleLegend v-if="isLegendSectionOpened" />
          <span class="subscribe-content__badge" v-else>Подписка</span>
        </div>
        <p class="subscribe-content__description">
          <template v-if="isLegendSectionOpened">
            Для тех, кто хочет сказать спасибо и помочь расти дальше
          </template>
          <template v-else>
            Выберите подписку на удобный срок и получите доступ к контенту Кэпитала:
          </template>
        </p>
      </header>

      <!-- Legend Section -->
      <template v-if="isLegendSectionOpened">
        <SubscribeItem
          v-if="legendSubscription"
          :price="legendSubscription.price"
          :value="legendSubscription.value"
          :currency="legendSubscription.currency"
          :days="legendSubscription.days"
          :tagStyle="'default'"
          :isActive="true"
        />
      </template>

      <!-- Regular Subscriptions -->
      <template v-else>
        <section class="subscribe-content__plans">
          <SubscribeItem
            v-for="sub in subscriptions"
            :key="sub.id"
            :price="sub.price"
            :value="sub.value"
            :currency="sub.currency"
            :days="sub.days"
            :tag="sub.tag"
            :tagStyle="sub.tagStyle"
            :isPon="sub.isPon"
            :isActive="selectedId == sub.id"
            @click="selectSubscription(sub.id)"
          />
        </section>
      </template>

      <!-- Benefits Grid -->
      <section class="subscribe-content__benefits">
        <SubscriptionBenefitsItem
          v-for="item in filteredBenefits"
          :key="item.iconName"
          class="subscribe-content__benefit-item"
          :title="item.title"
          :iconName="item.iconName"
          :text="item.description"
          :isLegend="item.isLegend"
          :isLegendColor="item.isLegendColor"
        />
      </section>
    </div>

    <!-- Actions Footer -->
    <footer class="subscribe-content__footer">
      <!-- Legend Payment Button -->
      <template v-if="isLegendSectionOpened && legendSubscription">
        <div class="subscribe-content__footer-box">
          <button
            v-if="!paymentUrl"
            class="subscribe-content__legend-btn"
            :style="`--bg-legend: url('${BG_LEGEND}')`"
            @click="activeLegendary()"
          >
            Стать Легендой на 12 месяцев
          </button>
          <a
            v-else
            class="subscribe-content__legend-btn"
            :style="`--bg-legend: url('${BG_LEGEND}')`"
            :href="paymentUrl"
            target="_blank"
          >
            Перейти к оплате
        </a>
        </div>
      </template>

      <!-- PoN -->
      <template v-else-if="selectedSubscription.isPon">
        <div class="subscribe-content__marketplace-buttons">
          <ButtonMain
            class="subscribe-content__marketplace-btn"
            :link="selectedSubscription.link"
            type="blue-border"
            target="_blank"
            width="100%"
          >
            Купить на OpenSea <LinkIcon class="subscribe-content__link-icon" />
          </ButtonMain>
          <ButtonMain
            class="subscribe-content__marketplace-btn"
            link="https://blur.io/eth/collection/proof-of-narnian-nft-og"
            type="blue-border"
            target="_blank"
            width="100%"
          >
            Купить на Blur <LinkIcon class="subscribe-content__link-icon" />
          </ButtonMain>
        </div>
        <ButtonMain
          class="subscribe-content__marketplace-btn"
          type="primary"
          @click="handlePonActivation"
          width="100%"
        >
          Активировать PoN
        </ButtonMain>
      </template>
      <template v-else>
        <template v-if="paymentUrl">
          <ButtonMain class="subscribe-content__telegram-btn" type="primary" :is="'a'" :link="paymentUrl" :target="'_blank'">
            Перейти к оплате
          </ButtonMain>
        </template>
        <template v-else>
          <ButtonMain
            class="subscribe-content__telegram-btn"
            type="primary"
            :is="'button'"
            @click="handleSubscriptionPayment"
          >
            {{ buttonText }}
          </ButtonMain>
        </template>
      </template>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

// Components
import SubscribeItem from "./SubscribeItem/SubscribeItem.vue";
import ButtonMain from "@shared/UI/Buttons/ButtonMain/ButtonMain.vue";
import SubscriptionBenefitsItem from "./SubscriptionBenefitsItem/SubscriptionBenefitsItem.vue";
import LogotypeCapital from "@shared/UI/Logotypes/LogotypeCapital/LogotypeCapital.vue";
import LinkIcon from "@shared/UI/SVG/LinkIcon.vue";
import CaplsuleLegend from "@shared/UI/Capsules/CaplsuleLegend/CaplsuleLegend.vue";

// Constants
import DROPDOWN_NAMES from "@shared/constants/dropdowns/dropdownsName.js";
import AUTH_SECTIONS_NAME from "@shared/constants/dropdowns/authSectionsName.js";
import { PATHS_NAME } from "@shared/routers/consts/pathsName.js";
import { backendUrl } from "@shared/constants/urls/appUrls";
import { fetchWithAuth } from "@shared/utils/auth";

const store = useStore();
const router = useRouter();
const emit = defineEmits();

const BG_LEGEND = require("@shared/images/backgrounds/legend_bg.png");
const selectedId = ref(null);
const paymentUrl = ref(null);

// Helper: склонение месяцев
const pluralizeMonths = (n) => {
  if (n === 1) return "месяц";
  if (n >= 2 && n <= 4) return "месяца";
  return "месяцев";
};

// Список преимуществ
const benefits = [
  { iconName: "alpha", title: "Альфа", description: "Рыночная аналитика, анализ проектов, инвест идеи, разбор ICO, и т.д." },
  { iconName: "podcasts", title: "Подкасты", description: "Три подкаста в неделю с фаундерами Drops Capital и уникальными гостями" },
  { iconName: "calendar", title: "Календарь", description: "Отмечаем все важные события крипторынка и мира" },
  { iconName: "forecasters", title: "Форкастеры", description: "Топовые голоса твиттера и телеграма на русском в удобном формате" },
  { iconName: "signal", title: "Сигналы", description: "Фиксируем идеи и их движения" },
  { iconName: "screener", title: "Скринер", description: "Отслеживаем динамику всех крипто-активов" },
  { iconName: "work", title: "Кэпитал Work", description: "Рабочий чат - минимум флуда, максимум пользы" },
  { iconName: "meme", title: "Кэпитал Meme", description: "Торговля и обсуждение мемкоинов" },
  { iconName: "alerts", title: "Кэпитал Алертс", description: "Канал с сигналами от коммьюнити" },
  { iconName: "holders", title: "PoN Holders", description: "Эксклюзивный чат для владельцев PoN NFT" },
  { iconName: "podcasts", title: "+1 Подкаст", description: "Дополнительные оффтоп подкасты и контент от Сергея с командой", isLegend: true },
  { iconName: "crown", title: "Легендарный никнейм", description: "Отметим вашу щедрость и особый статус на Кэпитале", isLegend: true, isLegendColor: true },
];

// Данные из store
const storeTariffs = computed(() => store.getters["HeaderSystem/tariffs"] || []);
const isLegendSectionOpened = computed(() => store.getters["HeaderSystem/isLegendSection"]);
const userData = computed(() => store.state.UserSystem.user || {});
const hasTelegram = computed(() => Boolean(userData.value.telegram?.trim()));
const isAuth = computed(() => store.getters["UserSystem/isAuthenticated"]);

// Фильтруем legend тариф
const legendSubscription = computed(() => {
  const t = storeTariffs.value.find((tariff) => tariff.code === "legend");
  if (!t) return null;
  const currency = t.currency === "USD" ? "$" : t.currency;
  const value = t.months && t.months > 0 ? `${(parseFloat(t.amount) / t.months).toFixed(2)} ${currency}/мес` : `${t.amount} ${currency}`;
  return { price: t.amount, value, currency, days: t.months || 12 };
});

// Подписки без legend
const subscriptions = computed(() => {
  const ponItem = {
    id: "pon",
    price: "0.34",
    value: "Разово",
    currency: "ETH",
    isPon: true,
    link: "https://opensea.io/collection/proof-of-narnian",
    tagStyle: "default",
  };

  const mapped = (storeTariffs.value || [])
    .filter((t) => t.code !== "legend")
    .map((t) => {
      const currency = t.currency === "USD" ? "$" : t.currency;
      const tag = t.code === "1year" ? "экономия 15%" : undefined;
      const tagStyle = t.code === "1year" ? "primary" : t.code === "2months" ? "secondary" : "default";
      const value = t.months && t.months > 0 ? `${(parseFloat(t.amount) / t.months).toFixed(2)} ${currency}/мес` : `${t.amount} ${currency}`;
      return { id: t.id, price: t.amount, value, currency, days: t.months, tag, tagStyle, tariff: t.code };
    });

  const sorted = mapped.sort((a, b) => b.days - a.days);
  return sorted.concat(ponItem);
});

// Текущая подписка
const selectedSubscription = computed(() => subscriptions.value.find((s) => s.id == selectedId.value) || {});

// --- Исправлено: фильтрация преимуществ ---
const filteredBenefits = computed(() => {
  if (isLegendSectionOpened.value) {
    return benefits.filter((item) => item.isLegend);
  }
  if (selectedSubscription.value.isPon) {
    return benefits.filter((item) => !item.isLegend);
  }
  return benefits.filter((item) => !item.isLegend && item.iconName !== "holders");
});

// Текст кнопки
const buttonText = computed(() => {
  const sub = selectedSubscription.value;
  if (sub?.isPon) return "Активировать PoN";
  if (sub?.days && !isNaN(sub.days)) return `Купить подписку на ${sub.days} ${pluralizeMonths(sub.days)}`;
  return "Оформить подписку";
});

// Lifecycle
onMounted(async () => {
  if (!storeTariffs.value.length) {
    try {
      await store.dispatch("HeaderSystem/fetchCryptoCloudTariffs");
    } catch (err) {
      console.error("Не удалось загрузить тарифы:", err);
    }
  }
  const defaultSub = subscriptions.value.find((s) => s.days === 12 && !s.isPon);
  selectedId.value = defaultSub ? defaultSub.id : subscriptions.value[0]?.id || null;
});

onBeforeUnmount(() => {
  sessionStorage.removeItem("isPonPageMove");
});

// Методы
const selectSubscription = (id) => {
  selectedId.value = id;
  sessionStorage.setItem("telegramWidgetCurrentID", String(id));
};

const createCryptoCloudPayment = async (tariff) => {
  console.log('PON CLOUD PAYMENT')
  const url = `${backendUrl}/api/payments/cryptocloud/create/`;
  const formData = new FormData();
  formData.append("tariff", tariff);
  try {
    const response = await fetchWithAuth(url, { method: "POST", body: formData });
    if (!response.ok) throw new Error("Ошибка при создании оплаты");
    console.log(response)
    const data = await response.json();
    console.log(data)
    if (data?.pay_url) {
      paymentUrl.value = data.pay_url;
      try {
        window.location.href = data.pay_url;
      } catch (_) {}
    }
  } catch (err) {
    console.error("Ошибка Cryptocloud:", err);
  }
};

const activeLegendary = () => createCryptoCloudPayment("legend");

const handlePonActivation = () => {
  if (isAuth.value) {
    emit("close");
    router.push({ name: PATHS_NAME.USER.SUBSCRIPTION_PURCHASE });
  } else {
    openAuth();
    sessionStorage.setItem("isPonPageMove", "true");
  }
};

const handleSubscriptionPayment = async () => {
  console.log('PON START')
  if (!isAuth.value) {
    openAuth();
    return;
  }
  if (selectedSubscription.value.tariff) {
    await createCryptoCloudPayment(selectedSubscription.value.tariff);
  }
};

const openAuth = () => {
  store.commit("AuthorizationSystem/toActiveSection", AUTH_SECTIONS_NAME.START_LOGIN);
  store.dispatch("Dropdowns/addNewDropdown", DROPDOWN_NAMES.auth);
};
</script>

<style lang="scss" scoped>
@import "./styles.scss";
</style>